<template>
	<div class="s-w-full s-border-t s-border-gray-300 s-rounded s-bg-white">
		<JFlex class="s-items-center s-p-4 s-gap-2">
			<JInputText :value="searchTerm" @input="handleInput($event)" :placeholder="$t('core.searchFiles')" />
			<input
				type="date"
				v-model="fromDate"
				@input="handleDateChange"
				placeholder="From Date"
				class="s-px-3 s-py-2 s-border s-border-gray-300 s-rounded focus:s-outline-none focus:s-ring-2 focus:s-ring-indigo-500"
			/>
			<input
				type="date"
				v-model="toDate"
				@input="handleDateChange"
				placeholder="To Date"
				class="s-px-3 s-py-2 s-border s-border-gray-300 s-rounded focus:s-outline-none focus:s-ring-2 focus:s-ring-indigo-500"
			/>
			<button class="s-p-1 s-appearance-none focus:s-outline-none" @click="clearSearch">
				<svg
					width="24"
					height="24"
					fill="none"
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path d="M6 18L18 6M6 6l12 12"></path>
				</svg>
			</button>
		</JFlex>

		<JSpinner v-if="loading" variant="indigo" type="dots" class="s-mx-auto s-mt-20 s-mb-20" :delay="0" />

		<JBox v-if="!loading && files.length > 0" class="s-border-t s-border-gray-300 s-p-4">
			<!-- Select All Header -->
			<div v-if="enableDownloadAsZip" class="s-flex s-items-center s-pb-2 s-mb-2 s-border-b s-border-gray-200">
				<input
					type="checkbox"
					:checked="allItemsSelected"
					:indeterminate.prop="someItemsSelected"
					@change="toggleSelectAll"
					class="s-ml-2 s-mr-3 s-cursor-pointer"
				/>
				<span class="s-text-gray-700 s-font-medium">Select All</span>
			</div>

			<div
				v-for="(item, i) in files"
				:key="i"
				class="s-py-2 s-my-2 hover:s-bg-indigo-100 s-cursor-pointer s-flex s-items-center"
				:class="{
					's-bg-indigo-200': enableDownloadAsZip && isItemSelected(item),
					's-bg-indigo-100': !enableDownloadAsZip && selectedFileIndex === i,
				}"
				@click="handleItemClick(item, i)"
			>
				<input
					v-if="enableDownloadAsZip"
					type="checkbox"
					:checked="isItemSelected(item)"
					@click.stop="toggleItemSelection(item)"
					class="s-ml-2 s-mr-3 s-cursor-pointer s-flex-shrink-0"
				/>
				<div :class="enableDownloadAsZip ? 's-flex-1' : 's-px-2 s-flex-1'">
					<div class="s-text-gray-900 s-font-medium">
						{{ item.fileName }}
					</div>
					<div class="s-text-gray-500 s-text-sm">
						{{ item.key }}
					</div>
				</div>
			</div>
		</JBox>
	</div>
</template>

<script>
import api from "@/api/api"
import { query } from "../../api/queries"
import debounce from "lodash.debounce"
import dayjs from "dayjs"
import { isHiddenKey } from "./hiddenFiles"

export default {
	props: {
		bucketName: {
			type: String,
			required: true,
		},
		allowedPrefixes: {
			type: Array,
			default: () => [],
		},
		enableDownloadAsZip: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			searchTerm: null,
			fromDate: null,
			toDate: null,
			files: [],
			selectedItems: [],
			selectedFileIndex: null,
			loading: false,
		}
	},
	computed: {
		allItemsSelected() {
			return this.files.length > 0 && this.selectedItems.length === this.files.length
		},
		someItemsSelected() {
			return this.selectedItems.length > 0 && this.selectedItems.length < this.files.length
		},
	},
	methods: {
		handleInput(value) {
			this.searchTerm = value
			this.debouncedSearchFile()
		},
		handleDateChange() {
			this.debouncedSearchFile()
		},
		debouncedSearchFile: debounce(function() {
			this.searchFiles()
		}, 500),

		async searchFiles() {
			this.loading = true
			try {
				const where = {
					keyPattern: this.searchTerm,
					allowedPrefixes: this.allowedPrefixes,
				}

				// Convert from date to start of day ISO string
				if (this.fromDate) {
					where.from = dayjs(this.fromDate)
						.startOf("day")
						.toISOString()
				}

				// Convert to date to end of day ISO string
				if (this.toDate) {
					where.to = dayjs(this.toDate)
						.endOf("day")
						.toISOString()
				}

				const variables = {
					bucketName: this.bucketName,
					where,
				}
				const res = await api({ query: query.searchFiles, variables })
				this.files = (res?.data?.searchFiles ?? []).filter(item => !isHiddenKey(item?.key))
			} finally {
				this.loading = false
			}
		},

		handleItemClick(item, index) {
			if (this.enableDownloadAsZip) {
				this.toggleItemSelection(item)
			} else {
				this.selectedFileIndex = index
				this.$emit("file:selected", item)
			}
		},
		toggleSelectAll() {
			if (this.allItemsSelected) {
				// Deselect all
				this.selectedItems = []
			} else {
				// Select all
				this.selectedItems = [...this.files]
			}
			this.$emit("items:selected", this.selectedItems)
		},
		isItemSelected(item) {
			return this.selectedItems.some(selected => selected.key === item.key)
		},
		toggleItemSelection(item) {
			const index = this.selectedItems.findIndex(selected => selected.key === item.key)
			if (index !== -1) {
				this.selectedItems.splice(index, 1)
			} else {
				this.selectedItems.push(item)
			}
			this.$emit("items:selected", this.selectedItems)
		},
		clearSearch() {
			this.searchTerm = null
			this.fromDate = null
			this.toDate = null
			this.selectedItems = []
			this.selectedFileIndex = null
			this.files = []
			this.loading = false
			this.$emit("items:selected", [])
		},
	},
}
</script>
